import { useCallback, useState } from 'react';
import { saveFingeringResult, type FingeringStorageResult } from '../utils/fingeringStorage';
import { createFingeringZip, extractFingeringZip, type ExtractedArchive } from '../utils/driveZipArchive';
import {
  ensureAppFolder,
  ensureSubFolder,
  uploadArchiveResumable,
  uploadPdfFile,
  ARCHIVE_SUBFOLDER_NAME,
  listOmfzFiles,
  downloadOmfzBlob,
  deleteOmfzFile,
  findPdfWebViewLink,
  getPdfWebViewLinkById,
  findOmfzByName,
  type DriveOmfzFile,
} from '../utils/driveApi';
import { ArchiveError } from '../types/driveArchive';
import { pickDriveFolder } from '../utils/drivePicker';
import type { DriveTargetFolder } from '../utils/driveTargetFolder';
import { markDriveSaved } from '../utils/driveSaveState';
import { setDrivePdfFileId, getDrivePdfFileId, clearDrivePdfFileId } from '../utils/drivePdfLink';
import { buildHistoryAppProperties } from '../utils/driveHistoryMeta';
import type { FingeringMode } from '../types/fingering';
import { useDriveAuth } from '../contexts/DriveAuthContext';

/**
 * issue #154: Drive のセッションが切れ、 再ログインしないと処理を続けられない状態。
 * 呼び出し側 (DriveLinkCopyButton) は instanceof で判別して再ログイン導線を出す。
 */
export class DriveAuthRequiredError extends Error {
  constructor(message = 'Drive のセッションが切れました') {
    super(message);
    this.name = 'DriveAuthRequiredError';
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
const GOOGLE_APP_ID = GOOGLE_CLIENT_ID.split('-')[0] || '';

export interface SaveOptions {
  /**
   * issue #152: **元タイトル** (アップロード名から拡張子を除いたもの)。 完成 PDF 名を渡しては
   * ならない (omfz / manifest に `_運指済` が二重付与される)。 呼出し側は resolveOriginalTitle
   * (fileNaming) で解決した値を渡すこと。
   */
  title: string;
  part: string;
  /** issue #78: 譜面モード。 fileName 構築で part を含むか否かを切替える (必須)。 */
  mode: FingeringMode;
  tuning?: string;
  appVersion?: string;
  source?: string;
  savedByDevice?: 'desktop' | 'mobile';
  parentFolderId?: string;
  /**
   * issue #66: 保存物の作成起源を識別 (optional、 後方互換)。
   * - 'manual': ユーザが「保存」ボタンで Drive に保存
   * - 'auto': バックエンドが長時間処理後に自動保存 (#65 plan で利用)
   * - undefined: createFingeringZip 側で未書込 → 復元時 'manual' default
   */
  createdBy?: 'manual' | 'auto';
}

export interface UseGoogleDriveSaverReturn {
  isConfigured: boolean;
  isSaving: boolean;
  isRestoring: boolean;
  error: string | null;
  /** 有効な Drive token を保持しているか (issue #35 M-3a, OR 判定。 ログアウト/再ログイン導線の表示に使う)。 */
  isLoggedIn: boolean;
  /** drive.file token が fresh か (issue #35 PR3 課題2-B → #123 で file 単一。 indicator「🟢=全機能可」用)。 */
  hasFullAccess: boolean;
  /** silent+consent 両失敗で token が取れず、明示的な再ログインが必要な状態 (issue #35 M-2)。 */
  needsReauth: boolean;
  saveFingering: (stored: FingeringStorageResult, opts: SaveOptions) => Promise<string>;
  pickFolder: () => Promise<DriveTargetFolder | null>;
  /** issue #71: parents 指定を廃止し全 .omfz を横断検索する。 引数は受け取らない。 */
  listHistory: () => Promise<DriveOmfzFile[]>;
  /**
   * issue #177: 復元は定義上 Drive 保存済み。 markDriveSaved でリンクコピーを活性化する。
   * pdfFileId (omfz appProperties 由来) があれば控え、 フォルダ移動後もリンクを引けるようにする。
   */
  restoreFromHistory: (fileId: string, pdfFileId?: string) => Promise<ExtractedArchive>;
  /** issue #138: Drive 履歴の .omfz を削除する。 元 PDF は残る (.omfz のみ削除)。 */
  deleteFromHistory: (fileId: string) => Promise<void>;
  /**
   * issue #140: 保存済み運指 PDF の Drive 共有リンク (webViewLink) を取得する。
   * target 指定時はそのフォルダ、 未指定時は既定 APP_FOLDER を探索先とする。 未保存なら null。
   */
  getPdfShareLink: (stored: FingeringStorageResult, target: DriveTargetFolder | null) => Promise<string | null>;
  /** 明示的な再ログイン (issue #35 M-2)。ボタン onClick の同期直下で呼ぶこと (popup blocker 回避)。 */
  reauthenticate: () => Promise<void>;
  /** 明示的サインアウト (issue #35 M-3a-1)。全 scope の token を破棄する。 */
  logout: () => void;
  resetError: () => void;
}

function detectDevice(): 'desktop' | 'mobile' {
  if (typeof navigator === 'undefined') return 'desktop';
  return /Mobi|Android|iPhone/.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

export function useGoogleDriveSaver(): UseGoogleDriveSaverReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // issue #79 Phase β: ログイン状態と token 取得操作は DriveAuthContext に集約。
  // 4 hook インスタンス間で自動同期される single source of truth。
  const auth = useDriveAuth();
  const isLoggedIn = auth.isLoggedIn;
  const hasFullAccess = auth.hasFullAccess;
  const [needsReauth, setNeedsReauth] = useState(false);

  const getToken = useCallback(async (): Promise<string> => {
    try {
      // ユーザー操作 (保存ボタン等) 同期直下の初回ログイン。 drive.file consent で token を
      // 取得する (issue #35 課題2-A → #123 で file 単一)。 silent を skip し consent 直行で
      // popup blocker を回避する (issue #35 P0 #57)。
      const token = await auth.signInInteractive();
      setNeedsReauth(false);
      return token;
    } catch (err) {
      // silent+consent 両失敗 (popup ブロック等)。明示的な再ログイン導線に切り替える。
      setNeedsReauth(true);
      throw err;
    }
  }, [auth]);

  // 明示的な再ログイン (M-2)。ボタン onClick 同期直下で呼ばれ、consent を強制する。
  const reauthenticate = useCallback(async (): Promise<void> => {
    await auth.signInInteractive();
    setNeedsReauth(false);
  }, [auth]);

  // 明示的サインアウト (M-3a-1)。全 scope の token cache を破棄する。
  const logout = useCallback((): void => {
    auth.logout();
    setNeedsReauth(false);
  }, [auth]);

  // issue #35 M-1: token + 401 リトライ用 refreshToken をまとめた DriveAuth を生成する。
  const buildFileAuth = useCallback(async () => {
    const token = await getToken();
    return {
      token,
      refreshToken: () => auth.refreshFileToken(),
    };
  }, [auth, getToken]);

  const saveFingering = useCallback(
    async (stored: FingeringStorageResult, opts: SaveOptions): Promise<string> => {
      setError(null);
      setIsSaving(true);
      try {
        const auth = await buildFileAuth();
        // zip 生成 (CPU) とフォルダ解決 (Drive API) は独立なので並列実行する。
        const [archive, folderId] = await Promise.all([
          createFingeringZip(stored, {
            title: opts.title,
            part: opts.part,
            // issue #78: mode は fileName の part 省略/含有分岐に必須
            mode: opts.mode,
            tuning: opts.tuning,
            appVersion: opts.appVersion ?? '2026-04-23',
            source: opts.source ?? window.location.origin + '/',
            savedByDevice: opts.savedByDevice ?? detectDevice(),
            // issue #66: createdBy が指定されれば manifest に書き込む (未指定なら undefined のまま渡す)
            createdBy: opts.createdBy,
          }),
          opts.parentFolderId ?? ensureAppFolder(auth),
        ]);
        // issue #103: 完成PDF (→指定フォルダ直下) と サブフォルダ「運指アーカイブ」解決は
        // 互いに独立なので並列実行する。 .omfz upload はサブフォルダ id に依存するため後続。
        const [pdfFileId, archiveFolderId] = await Promise.all([
          // 完成PDF → 指定フォルダ直下 (フロントDL名 stored.fileName と同一)。 fileId を後段で控える。
          uploadPdfFile({ ...auth, folderId, fileName: stored.fileName, blob: stored.blob }),
          // .omfz 隔離用サブフォルダを find-or-create
          ensureSubFolder(auth, folderId, ARCHIVE_SUBFOLDER_NAME),
        ]);
        // issue #152: 同一結果の再保存 (再生成→自動保存等) で .omfz が重複しないよう、
        // 同名既存を削除してから upload する (upsert)。 resumable upload は常に新規作成のため。
        const existingOmfzIds = await findOmfzByName(auth, archiveFolderId, archive.fileName);
        await Promise.all(existingOmfzIds.map((id) => deleteOmfzFile(auth, id)));
        const fileId = await uploadArchiveResumable({
          ...auth,
          folderId: archiveFolderId,
          fileName: archive.fileName,
          description: archive.manifest.description,
          blob: archive.blob,
          chunkSize: 1024 * 1024,
          // issue #169: 履歴一覧の「フルネーム / 総譜・パート譜 / パート」表示に使う構造化メタ。
          // issue #177: 完成PDFの fileId も omfz appProperties へ載せ、 復元時のリンク取得を堅牢化。
          appProperties: buildHistoryAppProperties(opts.title, opts.mode, opts.part, pdfFileId),
        });
        // issue #154: 保存成功 = Drive 上に完成 PDF が存在する。 フラグを永続化して
        // リロード後も「Drive保存済み」表示 / 共有リンクコピー活性を維持する。
        markDriveSaved(stored.fileName);
        // issue #177: 完成PDFの fileId を控え、 同一セッションのリンク取得をフォルダ探索非依存にする。
        setDrivePdfFileId(stored.fileName, pdfFileId);
        return fileId;
      } catch (err) {
        const msg =
          err instanceof ArchiveError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Drive への保存に失敗しました';
        setError(msg);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [buildFileAuth],
  );

  const pickFolder = useCallback(async (): Promise<DriveTargetFolder | null> => {
    const token = await getToken();
    return pickDriveFolder({ token, apiKey: GOOGLE_API_KEY, appId: GOOGLE_APP_ID });
  }, [getToken]);

  // issue #71: フッタ指定フォルダ / 固定 APP_FOLDER のどちらに保存された .omfz も
  // 一覧に乗せるため、 parents 指定なしで全 .omfz を横断検索する。
  // scope 'drive.file' により自アプリ作成分のみが可視となるため安全 (Drive API 仕様)。
  const listHistory = useCallback(async (): Promise<DriveOmfzFile[]> => {
    const auth = await buildFileAuth();
    return await listOmfzFiles(auth);
  }, [buildFileAuth]);

  const restoreFromHistory = useCallback(
    async (fileId: string, pdfFileId?: string): Promise<ExtractedArchive> => {
      setIsRestoring(true);
      try {
        const auth = await buildFileAuth();
        const blob = await downloadOmfzBlob(auth, fileId);
        const extracted = await extractFingeringZip(blob);
        // FingeringStorageResult (load 形) → SaveFingeringResultOptions (save 形) 変換: blob → pdfBlob
        await saveFingeringResult({
          pdfBlob: extracted.stored.blob,
          fileName: extracted.stored.fileName,
          // issue #152: 復元後の再生成も (元タイトル, part, mode) の純関数で同じ名前を再現できるよう、
          // extract で正規化済みの元タイトルを IndexedDB へ持ち越す。
          originalTitle: extracted.stored.originalTitle,
          audioBlob: extracted.stored.audioBlob,
          audioFileName: extracted.stored.audioFileName,
          fingeringJson: extracted.stored.fingeringJson,
          originalPdfBlob: extracted.stored.originalPdfBlob,
          mxlBlob: extracted.stored.mxlBlob,
          detectedBpm: extracted.stored.detectedBpm,
          // issue #81 追補: .omfz manifest 由来の mode を永続化し Drive 保存ボタンを enable に保つ。
          mode: extracted.stored.mode,
        });
        // issue #177: 復元＝定義上 Drive 保存済み。 フラグを立て共有リンクコピーを活性化する
        //（FingeringPage:366-368 の useEffect が storedSnapshot 更新時に driveSaved へ反映）。
        markDriveSaved(extracted.stored.fileName);
        // issue #177: omfz appProperties 由来の pdfFileId を控え、 フォルダ移動後もリンクが引ける。
        if (pdfFileId) setDrivePdfFileId(extracted.stored.fileName, pdfFileId);
        return extracted;
      } finally {
        setIsRestoring(false);
      }
    },
    [buildFileAuth],
  );

  // issue #138: Drive 履歴の .omfz を削除する。 buildFileAuth (401 リトライ配線) を通す。
  const deleteFromHistory = useCallback(async (fileId: string): Promise<void> => {
    const auth = await buildFileAuth();
    await deleteOmfzFile(auth, fileId);
  }, [buildFileAuth]);

  // issue #140: 保存済み運指 PDF の共有リンクを取得する。 保存先はフッタ指定 (target) を
  // 優先し、 未指定時のみ ensureAppFolder で既定フォルダを解決する (saveFingering と同順)。
  // issue #154: token 取得失敗 (silent+consent 両方 NG = セッション切れ) は DriveAuthRequiredError
  // に載せ替える。 コピー導線は「再ログイン」を出して復帰できるようにする。
  const getPdfShareLink = useCallback(
    async (stored: FingeringStorageResult, target: DriveTargetFolder | null): Promise<string | null> => {
      const auth = await buildFileAuth().catch(() => {
        throw new DriveAuthRequiredError();
      });
      // issue #177: 保存/復元時に控えた pdfFileId があれば fileId 直取り (フォルダ非依存)。
      const knownFileId = getDrivePdfFileId(stored.fileName);
      if (knownFileId) {
        const byId = await getPdfWebViewLinkById(auth, knownFileId);
        if (byId) return byId;
        // 削除/権限外で null → 陳腐化した控えを捨て、 従来のフォルダ探索へフォールバック。
        clearDrivePdfFileId(stored.fileName);
      }
      const folderId = target?.id ?? (await ensureAppFolder(auth));
      return findPdfWebViewLink(auth, folderId, stored.fileName);
    },
    [buildFileAuth],
  );

  return {
    isConfigured: !!GOOGLE_CLIENT_ID,
    isSaving,
    isRestoring,
    error,
    isLoggedIn,
    hasFullAccess,
    needsReauth,
    saveFingering,
    pickFolder,
    listHistory,
    restoreFromHistory,
    deleteFromHistory,
    getPdfShareLink,
    reauthenticate,
    logout,
    resetError: () => setError(null),
  };
}
